import { FilterListRounded } from "@mui/icons-material";
import { Badge, Button, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { ToggleButtonStatusSubmenu, ToggleButtonSubmenu } from "src/components";
import {
  ACTION_ITEM_STATUSES,
  ActiveActionItemStatuses,
  ClosedActionItemStatuses,
  TeamStrategyFilterByOption,
} from "src/constants";
import { useGetPlan } from "src/http";

interface Props {
  planGuid: string;
  dateTimeLocal?: string | null;
  dateTimeLocalUtcOffsetInMinutes?: number | null;
  filters: Record<TeamStrategyFilterByOption, string[]>;
  onFiltersChanged: (filters: Record<TeamStrategyFilterByOption, string[]>) => void;
}

const TeamStrategyFilterButton = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { data } = useGetPlan(
    props.planGuid,
    props.dateTimeLocal ?? undefined,
    props.dateTimeLocalUtcOffsetInMinutes ?? undefined,
  );

  function handleFilterByOptionChanged(options: string[]) {
    // Ignore other options that require a value selection from a submenu
    if (options.includes("All")) {
      props.onFiltersChanged({
        [TeamStrategyFilterByOption.Owner]: [],
        [TeamStrategyFilterByOption.Status]: [],
      });
    }
  }

  function handleFilterByValueChanged(option: TeamStrategyFilterByOption, value: string[]) {
    if (value != null) {
      props.onFiltersChanged({
        ...props.filters,
        [option]: value,
      });
    }
  }

  const ownerOptions = _.uniqBy(
    data?.blocks.flatMap((block) =>
      block.sections.flatMap((section) => section.actionItems.flatMap((actionItem) => actionItem.owners)),
    ),
    (owner) => owner.userId,
  ).sort();

  const appliedFilters = [TeamStrategyFilterByOption.Owner, TeamStrategyFilterByOption.Status].filter((x) => {
    return props.filters[x].length > 0;
  });

  const appliedFiltersCount = appliedFilters.reduce((sum, option) => {
    return sum + props.filters[option].length;
  }, 0);

  return (
    <>
      <Badge color="primary" variant="dot" invisible={appliedFilters.length === 0}>
        <Button variant="tertiary" startIcon={<FilterListRounded />} onClick={(e) => setMenuAnchor(e.currentTarget)}>
          {appliedFilters.length === 0
            ? "Filter"
            : appliedFiltersCount > 1
              ? `${appliedFiltersCount} Filters`
              : "1 Filter"}
        </Button>
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <ToggleButtonGroup
          value={appliedFilters.length > 0 ? appliedFilters : ["All"]}
          orientation="vertical"
          variant="menu"
          onChange={(_, option) => handleFilterByOptionChanged(option)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value="All">
            <Typography>All</Typography>
          </ToggleButton>

          <ToggleButtonStatusSubmenu
            value={TeamStrategyFilterByOption.Status}
            statuses={ACTION_ITEM_STATUSES}
            selectedStatusIds={props.filters[TeamStrategyFilterByOption.Status].map((x) => Number(x)) ?? []}
            activeStatusFilter={(status) => ActiveActionItemStatuses.includes(status.id)}
            closedStatusFilter={(status) => ClosedActionItemStatuses.includes(status.id)}
            onStatusSelected={(selectedStatusIds) =>
              handleFilterByValueChanged(
                TeamStrategyFilterByOption.Status,
                selectedStatusIds.map((x) => x.toString()),
              )
            }
          />

          <ToggleButtonSubmenu
            disabled={ownerOptions.length <= 1}
            label={TeamStrategyFilterByOption.Owner}
            value={TeamStrategyFilterByOption.Owner}
            suboptions={ownerOptions}
            selectedSuboptionValues={props.filters[TeamStrategyFilterByOption.Owner]}
            onSelected={(selectedSuboptionValues) =>
              handleFilterByValueChanged(TeamStrategyFilterByOption.Owner, selectedSuboptionValues)
            }
            suboptionLabelSelector={(suboption) => suboption.displayName}
            suboptionValueSelector={(suboption) => suboption.userId}
          />
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { TeamStrategyFilterButton };
